import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import { THERMOBOX_FORM_NAME, THERMOBOX_TABLE_ROWS_NAMES } from './constants';
import { thousandSeparator } from '../reqs-filter/utils';
import { getLocalStorageItem } from '../../utils/localStorage';

export const selectThermoboxModalDomain = state => state.thermoboxModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};
export const selectProfileModalDomain = state => state.profileModalReducer || {};

export const getThermoboxInfoSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ thermoboxInfo }) => {
		if (!thermoboxInfo) return [];

		return Object.entries(THERMOBOX_TABLE_ROWS_NAMES).reduce((result, [key, value]) => {
			if (key === 'partner') {
				result.push({
					id: key,
					title: value,
					value: thermoboxInfo.partner.name || '-',
				});

				return result;
			}

			if (key === 'outer') {
				result.push({
					id: key,
					title: value,
					value: {
						length: thermoboxInfo.outer_length ? thousandSeparator(thermoboxInfo.outer_length) : '',
						width: thermoboxInfo.outer_width ? thousandSeparator(thermoboxInfo.outer_width) : '',
						height: thermoboxInfo.outer_height ? thousandSeparator(thermoboxInfo.outer_height) : '',
					},
				});

				return result;
			}

			if (key === 'outerVolume') {
				result.push({
					id: key,
					title: value,
					value: thermoboxInfo.outer_volume ? `${thousandSeparator(thermoboxInfo.outer_volume, 3)} м³` : '-',
				});

				return result;
			}

			if (key === 'inner') {
				result.push({
					id: key,
					title: value,
					value: {
						length: thermoboxInfo.inner_length ? thousandSeparator(thermoboxInfo.inner_length) : '',
						width: thermoboxInfo.inner_width ? thousandSeparator(thermoboxInfo.inner_width) : '',
						height: thermoboxInfo.inner_height ? thousandSeparator(thermoboxInfo.inner_height) : '',
					},
				});

				return result;
			}

			if (key === 'innerVolume') {
				result.push({
					id: key,
					title: value,
					value: thermoboxInfo.inner_volume ? `${thousandSeparator(thermoboxInfo.inner_volume, 3)} м³` : '-',
				});

				return result;
			}

			if (key === 'emptyWeight') {
				result.push({
					id: key,
					title: value,
					value: thermoboxInfo.empty_weight ? `${thousandSeparator(thermoboxInfo.empty_weight)} кг` : '-',
				});

				return result;
			}

			if (
				key === 'pallet' &&
				thermoboxInfo.pallet_length &&
				thermoboxInfo.pallet_width &&
				thermoboxInfo.pallet_height
			) {
				result.push({
					id: key,
					title: value,
					value: {
						length: thermoboxInfo.pallet_length ? thousandSeparator(thermoboxInfo.pallet_length) : '',
						width: thermoboxInfo.pallet_width ? thousandSeparator(thermoboxInfo.pallet_width) : '',
						height: thermoboxInfo.pallet_height ? thousandSeparator(thermoboxInfo.pallet_height) : '',
					},
				});

				return result;
			}

			if (key === 'palletWeight' && thermoboxInfo.pallet_weight) {
				result.push({
					id: key,
					title: value,
					value: thermoboxInfo.pallet_weight ? `${thousandSeparator(thermoboxInfo.pallet_weight)} кг` : '-',
				});

				return result;
			}

			if (key === 'totalHeight') {
				result.push({
					id: key,
					title: value,
					value: thermoboxInfo.total_height ? `${thousandSeparator(thermoboxInfo.total_height)} см` : '-',
				});

				return result;
			}

			if (key === 'totalWeight') {
				result.push({
					id: key,
					title: value,
					value: thermoboxInfo.total_weight ? `${thousandSeparator(thermoboxInfo.total_weight)} кг` : '-',
				});

				return result;
			}

			if (key === 'author') {
				result.push({
					id: key,
					title: value,
					value: {
						name: `${thermoboxInfo?.user?.last_name || ''} ${thermoboxInfo?.user?.first_name || ''}`,
						role: thermoboxInfo?.user?.role?.name || '',
					},
				});

				return result;
			}

			return result;
		}, []);
	});

export const getIsThermoboxInfoLoadingSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ isThermoboxInfoLoading }) => isThermoboxInfoLoading);

export const getIsThermoboxCreatingSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ isThermoboxCreating }) => isThermoboxCreating);

export const getIsThermoboxUpdatingSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ isThermoboxUpdating }) => isThermoboxUpdating);

export const getIsThermoboxDeletingSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ isThermoboxDeleting }) => isThermoboxDeleting);

export const getFormInitialValuesSelector = () =>
	createSelector(selectThermoboxModalDomain, selectProfileModalDomain, ({ thermoboxInfo }, { userInfo }) => {
		if (!thermoboxInfo) {
			const companyId = getLocalStorageItem('user_data')?.companyId || userInfo?.company?.id;

			return {
				partner_id: companyId,
			};
		}

		return {
			...thermoboxInfo,
			partner_id: thermoboxInfo.partner?.id,
		};
	});

export const getFormIsInvalidSelector = () => createSelector(isInvalid(THERMOBOX_FORM_NAME), isInvalid => isInvalid);

export const getDataForSaveSelector = () =>
	createSelector(getFormValues(THERMOBOX_FORM_NAME), formValues => formValues);

export const getThermoboxNameSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ thermoboxInfo }) => thermoboxInfo?.name ?? '');

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox');
	});

export const getIsThermoboxAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox' && query?.mode === 'add');
	});

export const getIsThermoboxEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox' && query?.mode === 'edit');
	});

export const getActiveThermoboxIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'thermobox' ? query?.id : null;
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'thermobox' && query?.id && query?.tab === 'audit');
	});

export const getPartnersOptionsSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ partners }) =>
		partners.map(partner => ({
			key: partner.id,
			value: partner.id,
			text: partner.name,
		})),
	);

export const getIsPartnersLoadingSelector = () =>
	createSelector(selectThermoboxModalDomain, ({ isPartnersLoading }) => isPartnersLoading);
